import React from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, Clock, User, ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

// Full blog articles with complete content
const fullArticles = {
  'automate-follow-up-local-business': {
    id: 1,
    title: "5 Ways to Automate Follow-Up Without Lifting a Finger",
    excerpt: "Discover how successful local businesses are using automation to nurture leads and convert more customers while they sleep.",
    category: "Automation",
    readTime: "8 min read",
    date: "2024-03-15",
    author: "Sarah Mitchell",
    image: "https://images.pexels.com/photos/171198/pexels-photo-171198.jpeg?w=1200&h=600&fit=crop&crop=center",
    content: `
      <p class="lead">Let's be honest: following up with every single lead manually is impossible when you're actually running a business. You're out on jobs, dealing with customers, managing staff, and handling a thousand other things. But here's the problem — 73% of leads never get followed up, and you're losing thousands in potential revenue.</p>

      <p>The good news? Automation can handle all your follow-up for you, and it works even better than doing it manually because it never forgets, never gets tired, and never misses a beat.</p>

      <h2>1. Instant Auto-Response When Someone Enquires</h2>
      
      <p>Think about the last time you filled out a contact form on a website. What happened? If you're like most people, you probably waited hours (or days) for a response. And if you were actively looking for a service, you probably moved on to the next business.</p>

      <p><strong>The solution:</strong> Set up an automated instant response that goes out the second someone submits a form or sends you a message. This isn't just a generic "We received your message" — make it personal, acknowledge their specific need, and let them know exactly when they'll hear from you.</p>

      <div class="example-box">
        <h4>Example Auto-Response:</h4>
        <p><em>"Hi [Name]! Thanks for your enquiry about [Service]. We're excited to help you with [their specific need]. I'll personally review your request and get back to you within 2 hours with a quote and availability. In the meantime, here's a link to see our recent work: [Portfolio Link]. - Sarah"</em></p>
      </div>

      <p>This immediately sets you apart from competitors who respond days later (or never). You've acknowledged them, shown professionalism, and kept them engaged.</p>

      <h2>2. The 24-Hour Follow-Up Sequence</h2>

      <p>Not every lead converts immediately. Some need to think about it, check with their partner, or wait until payday. But without follow-up, 80% of these "warm" leads go cold and choose your competitor instead.</p>

      <p><strong>Here's what to automate:</strong></p>

      <ul class="checklist">
        <li><strong>Hour 0:</strong> Instant auto-response (see above)</li>
        <li><strong>Hour 4:</strong> "Just checking you got my message" follow-up</li>
        <li><strong>Hour 24:</strong> "Quick question about your project" with a specific question to re-engage</li>
        <li><strong>Day 3:</strong> Case study or testimonial showing similar work you've done</li>
        <li><strong>Day 7:</strong> Time-sensitive offer or seasonal reminder</li>
      </ul>

      <p>Each message should add value — not just "following up". Share a tip, answer a common question, or send a relevant case study.</p>

      <h2>3. Missed Call Text-Back</h2>

      <p>Here's a sobering stat: 62% of callers won't call back if you miss their call. They'll just move on to the next business in the Google results.</p>

      <p><strong>The automation fix:</strong> When you miss a call, an automated SMS goes out within 30 seconds:</p>

      <div class="example-box">
        <p><em>"Hi, sorry I missed your call! This is Sarah from [Business Name]. I'm with a customer right now but I'd love to help you. Can you text me what you need? Or click here to book a call back: [Link]. I'll respond within the hour. Thanks!"</em></p>
      </div>

      <p>This simple automation has helped our clients capture thousands of leads that would have otherwise been lost. The best part? It works 24/7, even when you're asleep or on holiday.</p>

      <h2>4. Post-Job Review Request</h2>

      <p>Most businesses know they should be getting more Google reviews, but actually remembering to ask after every job? That's where everyone falls down.</p>

      <p><strong>Automate this:</strong> 24 hours after completing a job, an automated email or SMS goes out:</p>

      <div class="example-box">
        <p><em>"Hi [Name], I hope you're happy with [the work we did]. It was great working with you! If you have 60 seconds, I'd really appreciate if you could leave us a Google review. It helps us so much and lets other local customers know what to expect. Here's the link: [Direct Google Review Link]. Thanks! - Sarah"</em></p>
      </div>

      <p>Our clients using this automation average 15-20 new reviews per month, compared to 2-3 reviews for businesses who ask manually.</p>

      <h2>5. Re-Engagement for Lost Leads</h2>

      <p>You know those leads who enquired 2-3 months ago but never booked? They're not dead — they probably just got busy, forgot, or weren't ready at the time.</p>

      <p><strong>Here's the automation:</strong> 60-90 days after their initial enquiry, send a friendly "just checking in" message with a new angle:</p>

      <ul>
        <li>Seasonal reminder (e.g., "Spring is coming — ready to tackle that garden project?")</li>
        <li>New service announcement</li>
        <li>Customer success story similar to their needs</li>
        <li>Limited-time offer or availability update</li>
      </ul>

      <p>This "warm up the cold leads" automation typically converts 10-15% of old leads into bookings — that's pure profit from people who'd already forgotten about you.</p>

      <h2>The Bottom Line</h2>

      <p>You don't need to spend hours every day manually following up with leads. With proper automation, you can:</p>

      <ul class="checklist">
        <li>Respond to enquiries instantly (even at 3am)</li>
        <li>Never let a warm lead go cold</li>
        <li>Capture missed call opportunities</li>
        <li>Build your review count automatically</li>
        <li>Revive old leads without lifting a finger</li>
      </ul>

      <p>The businesses winning in your local area? They're not necessarily better than you — they're just better at following up. And now you can be too.</p>

      <div class="cta-box">
        <h3>Ready to Automate Your Follow-Up?</h3>
        <p>Start your 14-day trial and get all 5 of these automation workflows pre-built and ready to go. No technical knowledge needed.</p>
      </div>
    `
  },
  'google-reviews-complete-guide': {
    id: 2,
    title: "The Complete Guide to Getting Google Reviews (Without Being Pushy)",
    excerpt: "92% of consumers read online reviews before choosing a local business. Here's how to build a 5-star reputation.",
    category: "Reviews",
    readTime: "10 min read",
    date: "2024-03-10",
    author: "James Thompson",
    image: "https://images.pexels.com/photos/4348401/pexels-photo-4348401.jpeg?w=1200&h=600&fit=crop&crop=center",
    content: `
      <p class="lead">Let's talk about something that makes most business owners uncomfortable: asking for reviews. You know you need them (92% of consumers read reviews before choosing a local business), but actually asking feels awkward, pushy, or like begging. Here's the good news — it doesn't have to be.</p>

      <p>In this guide, I'll show you exactly how to get consistent 5-star Google reviews without feeling salesy, and more importantly, how to automate the entire process so you never have to remember to ask again.</p>

      <h2>Why Google Reviews Matter (More Than You Think)</h2>

      <p>Before we get into the "how", let's talk about why this matters:</p>

      <ul>
        <li><strong>Local Search Rankings:</strong> Google's algorithm heavily weights reviews when deciding who shows up in the "Local Pack" (those 3 businesses at the top). More reviews + higher ratings = better visibility.</li>
        <li><strong>Conversion Rates:</strong> A business with 50+ reviews converts 2-3x more website visitors than one with 5 reviews. People trust social proof.</li>
        <li><strong>Higher Prices:</strong> Businesses with 4.5+ star ratings can charge 10-20% more because customers perceive them as more valuable.</li>
        <li><strong>Less Price Shopping:</strong> When you have tons of reviews, people stop comparing quotes and just want to book you.</li>
      </ul>

      <p>Bottom line: Reviews = money. Let's get you more of them.</p>

      <h2>The #1 Mistake Everyone Makes</h2>

      <p>Most businesses wait until they're done with the job and then... forget to ask. Or they remember 3 weeks later and send an awkward text. Or they hand out business cards saying "Please review us!" (which no one ever does).</p>

      <p><strong>The problem?</strong> Timing. By the time you remember to ask, your customer has moved on mentally and the moment is lost.</p>

      <p><strong>The solution?</strong> Ask at the perfect moment, which is 24-48 hours after completing the job, when they're still happy but have had time to use/see the results of your work.</p>

      <h2>The Perfect Review Request (Copy This)</h2>

      <p>Here's the exact message template that gets a 40-50% response rate:</p>

      <div class="example-box">
        <p><strong>Subject/Opening:</strong> "Quick favour?"</p>
        <p><em>"Hi [Name],</em></p>
        <p><em>I hope you're happy with [specific work you did]. It was great working with you!</em></p>
        <p><em>If you have 60 seconds, I'd really appreciate if you could leave us a Google review. It helps us SO much — it's honestly how most of our new customers find us, and it lets them know what to expect.</em></p>
        <p><em>Here's the direct link: [Google Review Link]</em></p>
        <p><em>Thanks so much!</em><br/>
        <em>[Your Name]"</em></p>
      </div>

      <p><strong>Why this works:</strong></p>
      <ul class="checklist">
        <li>It's personal (you mention the specific job)</li>
        <li>It's not demanding (you're asking for a favour, not demanding one)</li>
        <li>It explains WHY (helps other customers, not just you)</li>
        <li>It's easy (direct link, 60 seconds)</li>
        <li>It's short (no one wants to read a novel)</li>
      </ul>

      <h2>How to Get Your Google Review Link</h2>

      <p>This is the most common question I get. Here's how:</p>

      <ol>
        <li>Go to Google and search for your business name</li>
        <li>Click on your business listing</li>
        <li>Look for "Get more reviews" or "Share review form"</li>
        <li>Copy that link — it goes straight to the review form</li>
      </ol>

      <p>Alternative: Use a link shortener (like bit.ly) to make it cleaner: <em>bit.ly/YourBusinessReview</em></p>

      <h2>The 3 Types of Customers (And How to Ask Each One)</h2>

      <h3>1. The Happy Customer (Your Low-Hanging Fruit)</h3>
      <p>These are the customers who said "This is brilliant!" or "You're a life-saver!" or gave you an unsolicited compliment.</p>

      <p><strong>For these people, ask immediately in person:</strong><br/>
      "I'm so glad you're happy! Quick question — if you have a minute later today, would you mind leaving us a Google review? It honestly helps us so much. Here, let me text you the link right now so you don't forget."</p>

      <p>Do it right there, get their number, and send the link. Strike while the iron is hot.</p>

      <h3>2. The Satisfied But Quiet Customer</h3>
      <p>Most of your customers fall here — they're happy with the work, but not gushing. They'd leave a review if asked, but won't think to do it on their own.</p>

      <p><strong>For these people, use the automated 24-hour follow-up</strong> (see template above). The key is making it easy and explaining why it matters.</p>

      <h3>3. The Neutral/Unhappy Customer</h3>
      <p>Don't ask these people for reviews (yet). Instead, reach out to fix any issues first:<br/>
      "I noticed you seemed [concerned/not 100% happy] with [specific thing]. I really want to make this right — can we chat about how to fix it?"</p>

      <p>Once you've resolved it, THEN you can ask for a review. In fact, turned-around customers often leave the BEST reviews because they appreciate that you cared enough to fix the problem.</p>

      <h2>Automation: Set It and Forget It</h2>

      <p>Manually sending review requests after every job is exhausting and you'll forget half the time. Here's how to automate it:</p>

      <h3>Step 1: Create a Trigger</h3>
      <p>Set up automation to trigger 24 hours after you mark a job as "complete" in your CRM.</p>

      <h3>Step 2: Send the Message</h3>
      <p>The system automatically sends your review request template via email or SMS.</p>

      <h3>Step 3: Follow Up If Needed</h3>
      <p>If they don't leave a review within 3 days, send one gentle reminder: "Just wanted to make sure you got my message about the review — no pressure at all! Thanks either way."</p>

      <p>That's it. You do nothing, and reviews roll in consistently.</p>

      <h2>What If You Get a Bad Review?</h2>

      <p>First, don't panic. One bad review among 50 good ones actually increases trust (no business is perfect, and customers know that).</p>

      <p><strong>How to respond to negative reviews:</strong></p>

      <ol>
        <li><strong>Respond quickly</strong> (within 24 hours shows you care)</li>
        <li><strong>Acknowledge their concern</strong> ("I'm sorry you had this experience")</li>
        <li><strong>Take it offline</strong> ("I'd love to discuss this with you directly — please call me at...")</li>
        <li><strong>Show you're taking action</strong> ("We've already implemented X to prevent this")</li>
        <li><strong>End professionally</strong> ("We appreciate your feedback and hope to serve you better in future")</li>
      </ol>

      <p>Future customers reading your response will see that you handle complaints professionally, which actually builds trust.</p>

      <h2>Advanced Tip: The Review Showcase</h2>

      <p>Once you have 20+ reviews, add a "Reviews" section to your website showing your best testimonials. This serves two purposes:</p>

      <ul class="checklist">
        <li>Builds trust with new website visitors</li>
        <li>Encourages more reviews (social proof: "everyone else is leaving reviews, I should too")</li>
      </ul>

      <h2>The Review Request Timeline (Your Action Plan)</h2>

      <table class="simple-table">
        <tr>
          <th>When</th>
          <th>What to Do</th>
        </tr>
        <tr>
          <td>During the job</td>
          <td>Deliver exceptional service (obvious but worth saying)</td>
        </tr>
        <tr>
          <td>At completion</td>
          <td>If they're clearly happy, ask in person</td>
        </tr>
        <tr>
          <td>24 hours later</td>
          <td>Automated review request email/SMS goes out</td>
        </tr>
        <tr>
          <td>3 days later</td>
          <td>If no review, send gentle one-time reminder</td>
        </tr>
        <tr>
          <td>Within 24 hours of review</td>
          <td>Thank them publicly (respond to their Google review)</td>
        </tr>
      </table>

      <h2>Quick Wins You Can Implement Today</h2>

      <ol>
        <li>Get your Google Review link and save it somewhere easy to access</li>
        <li>Write your review request template (use the example above)</li>
        <li>Identify your 3 happiest recent customers and text them the link RIGHT NOW</li>
        <li>Set up automation to send review requests 24 hours after job completion</li>
        <li>Respond to every review you get (good or bad) within 24 hours</li>
      </ol>

      <h2>The Bottom Line</h2>

      <p>Getting Google reviews isn't pushy — it's giving happy customers an easy way to help other people find a great business (yours). Most people WANT to help, they just need to be asked and given a simple way to do it.</p>

      <p>Start with automation, use the templates provided, and watch your review count climb. Within 3-6 months, you'll have 50+ reviews and be dominating your local search results.</p>

      <div class="cta-box">
        <h3>Want the Review Request Automation Pre-Built?</h3>
        <p>Our platform includes automated review requests, reminders, and a review showcase widget for your website. Set it up once, get reviews forever.</p>
      </div>
    `
  },
  'sms-marketing-local-business': {
    id: 3,
    title: "SMS Marketing for Local Businesses: The Ultimate Guide",
    excerpt: "SMS has a 98% open rate compared to 20% for email. Learn how to use it without annoying customers.",
    category: "SMS Marketing",
    readTime: "7 min read",
    date: "2024-03-05",
    author: "Emma Clarke",
    image: "https://images.pexels.com/photos/699122/pexels-photo-699122.jpeg?w=1200&h=600&fit=crop&crop=center",
    content: `
      <p class="lead">Email marketing is dead. Well, not completely dead, but let's be honest — when's the last time you opened a marketing email immediately? Now, when's the last time you ignored a text message? Exactly.</p>

      <p>SMS marketing has a 98% open rate (compared to 20% for email), and 90% of messages are read within 3 minutes. For local businesses where timing matters — emergency calls, last-minute availability, appointment reminders — SMS is a game-changer.</p>

      <p>But here's the thing: SMS marketing can also be incredibly annoying if done wrong. Send too many messages or the wrong type of messages, and customers will hate you. Do it right, and it becomes your most powerful marketing tool.</p>

      <h2>When SMS Marketing Works (And When It Doesn't)</h2>

      <h3>✅ Perfect For:</h3>
      <ul>
        <li><strong>Appointment reminders:</strong> "Hi [Name], reminder you have an appointment tomorrow at 2pm. Reply Y to confirm or N to reschedule."</li>
        <li><strong>Time-sensitive offers:</strong> "We have a last-minute cancellation today at 3pm. Want it? 20% off. Reply YES to book."</li>
        <li><strong>Booking confirmations:</strong> "Your booking for March 15 at 10am is confirmed! Here's the address: [Link]"</li>
        <li><strong>Order/service updates:</strong> "Your order is ready for collection!" or "We're 10 minutes away!"</li>
        <li><strong>Follow-up after service:</strong> "How did everything go yesterday? Reply if you need anything!"</li>
      </ul>

      <h3>❌ Terrible For:</h3>
      <ul>
        <li>Daily promotions (too spammy)</li>
        <li>Long-form content (use email for that)</li>
        <li>Generic mass messages to your entire list</li>
        <li>Anything that feels like an ad</li>
      </ul>

      <p><strong>The golden rule:</strong> Only send SMS when it's timely, relevant, and valuable to the recipient. If they'd be annoyed to receive it, don't send it.</p>

      <h2>The 5 SMS Campaigns Every Local Business Should Run</h2>

      <h3>1. Appointment Reminders (The Non-Negotiable)</h3>
      <p>If you're not sending automated SMS appointment reminders, you're losing 20% of your revenue to no-shows. This is the easiest win in SMS marketing.</p>

      <div class="example-box">
        <p><strong>24 hours before appointment:</strong></p>
        <p><em>"Hi [Name]! Reminder: You have an appointment with [Business] tomorrow at [Time]. Reply Y to confirm, N to reschedule, or call [Phone] with questions. See you tomorrow!"</em></p>
        
        <p><strong>2 hours before appointment:</strong></p>
        <p><em>"Your appointment is in 2 hours at [Time]. Here's our address: [Link to Google Maps]. Looking forward to seeing you!"</em></p>
      </div>

      <p>This alone reduces no-shows by 40-60%. Set it up once, save thousands.</p>

      <h3>2. Last-Minute Availability</h3>
      <p>Got a cancellation? Don't let that slot go to waste. Text your waitlist or recent customers.</p>

      <div class="example-box">
        <p><em>"Hi [Name], we have a last-minute opening tomorrow at 11am. Want it? 15% off for the short notice. Reply YES to claim it or STOP to opt out of these messages."</em></p>
      </div>

      <p>These convert at 30-40% because they're timely and feel exclusive.</p>

      <h3>3. Post-Service Follow-Up</h3>
      <p>24-48 hours after completing a job, send a quick check-in. This shows you care, catches any issues early, and opens the door to request a review.</p>

      <div class="example-box">
        <p><em>"Hi [Name], just wanted to check everything went well with [service] yesterday? Let me know if you need anything else! - [Your Name]"</em></p>
      </div>

      <p>If they respond positively, that's your cue to ask for a review (in a follow-up message, not this one).</p>

      <h3>4. Seasonal Reminders</h3>
      <p>For service-based businesses with seasonal work, SMS reminders are perfect for bringing customers back.</p>

      <div class="example-box">
        <p><em>"Hi [Name], it's been 6 months since we serviced your boiler. Book your annual check before winter hits and get 10% off. Book here: [Link]"</em></p>
      </div>

      <p>Send these 2-4 times per year maximum, and only to customers who've used your service before.</p>

      <h3>5. VIP/Loyalty Updates</h3>
      <p>If you have a loyalty program or VIP customers, keep them in the loop with exclusive SMS updates.</p>

      <div class="example-box">
        <p><em>"Hi [Name], as a VIP member you get first access to our new [service/product] launching next week. Want to be first? Reply YES for early booking."</em></p>
      </div>

      <p>Make it feel exclusive and special, not like a mass marketing blast.</p>

      <h2>The Technical Stuff (Made Simple)</h2>

      <h3>Getting Customer Phone Numbers (The Right Way)</h3>
      <p>You can't just buy a list of phone numbers and start texting (that's illegal in most places). Here's how to build your SMS list properly:</p>

      <ul>
        <li><strong>At booking:</strong> "May we send you appointment reminders via text? (Recommended)" ← Make it opt-in</li>
        <li><strong>On your website:</strong> "Get instant quotes via text: [Phone Number Input]"</li>
        <li><strong>After first service:</strong> "Want SMS reminders and special offers? Reply JOIN to opt in."</li>
        <li><strong>In-person:</strong> "Would you like text reminders for your next appointment?"</li>
      </ul>

      <p><strong>Legal requirement:</strong> Every SMS must include an opt-out option: "Reply STOP to opt out"</p>

      <h3>SMS vs MMS (When to Use Pictures)</h3>
      <ul>
        <li><strong>SMS:</strong> Text only, works on all phones, slightly cheaper</li>
        <li><strong>MMS:</strong> Can include images, great for showing before/after photos or appointment details</li>
      </ul>

      <p>For most local businesses, SMS is enough. But if you're in a visual industry (hair, beauty, landscaping, home improvement), MMS before/after photos can be powerful.</p>

      <h2>The Frequency Rule (Don't Blow It By Being Spammy)</h2>

      <p><strong>Maximum frequency guidelines:</strong></p>
      <ul>
        <li><strong>Transactional messages</strong> (reminders, confirmations): As needed</li>
        <li><strong>Promotional messages:</strong> Maximum 2-4 per month</li>
        <li><strong>Service updates:</strong> Only when genuinely relevant</li>
      </ul>

      <p>When in doubt, send less. It's better to be too quiet than too loud.</p>

      <h2>Real Results From Real Businesses</h2>

      <div class="stat-box">
        <p><strong>Dentist Practice (Glasgow):</strong></p>
        <p>Reduced no-shows from 18% to 4% with SMS reminders<br/>
        <em>Annual impact: £24,000 saved in wasted appointment slots</em></p>
      </div>

      <div class="stat-box">
        <p><strong>Plumbing Business (Manchester):</strong></p>
        <p>Last-minute availability texts converted 38% of recipients<br/>
        <em>Monthly impact: 15 extra jobs booked, £4,500 additional revenue</em></p>
      </div>

      <div class="stat-box">
        <p><strong>Hair Salon (Bristol):</strong></p>
        <p>Seasonal SMS reminders brought back 41% of lapsed customers<br/>
        <em>Quarterly impact: 60 rebookings without any other marketing spend</em></p>
      </div>

      <h2>Common Mistakes to Avoid</h2>

      <ol>
        <li><strong>No clear call-to-action:</strong> Every SMS should tell them exactly what to do next</li>
        <li><strong>Too formal:</strong> SMS is casual. Write like you'd text a friend</li>
        <li><strong>No personalization:</strong> Use their name and reference their specific situation</li>
        <li><strong>Sending at weird times:</strong> Stick to 9am-8pm, never weekends for promotional messages</li>
        <li><strong>No opt-out option:</strong> Always include "Reply STOP to opt out" (it's the law)</li>
        <li><strong>Too long:</strong> Keep it under 160 characters when possible (one SMS)</li>
        <li><strong>No tracking:</strong> Monitor open rates, click rates, and opt-out rates</li>
      </ol>

      <h2>Quick-Start Action Plan</h2>

      <p><strong>Week 1: Set Up Appointment Reminders</strong></p>
      <ul class="checklist">
        <li>Get customer consent to receive texts</li>
        <li>Set up automated 24-hour and 2-hour reminders</li>
        <li>Track no-show rate before and after</li>
      </ul>

      <p><strong>Week 2: Post-Service Follow-Up</strong></p>
      <ul class="checklist">
        <li>Create follow-up message template</li>
        <li>Set automation for 24-48 hours post-service</li>
        <li>Monitor responses and address issues quickly</li>
      </ul>

      <p><strong>Week 3: Last-Minute Availability</strong></p>
      <ul class="checklist">
        <li>Create a "VIP last-minute" customer segment</li>
        <li>Write your cancellation offer template</li>
        <li>Test it next time you have a cancellation</li>
      </ul>

      <p><strong>Week 4: Seasonal Campaign</strong></p>
      <ul class="checklist">
        <li>Identify your seasonal services</li>
        <li>Plan quarterly reminder campaigns</li>
        <li>Set up automation so you don't have to remember</li>
      </ul>

      <h2>The Bottom Line</h2>

      <p>SMS marketing is the most powerful tool in your local business arsenal — IF you use it right. Stick to timely, relevant, valuable messages, respect your customers' attention, and you'll see:</p>

      <ul class="checklist">
        <li>40-60% reduction in no-shows</li>
        <li>30-40% conversion on last-minute offers</li>
        <li>Higher customer satisfaction and retention</li>
        <li>More consistent bookings without extra advertising spend</li>
      </ul>

      <p>Start with appointment reminders (the low-hanging fruit), then expand from there.</p>

      <div class="cta-box">
        <h3>Ready to Set Up SMS Marketing?</h3>
        <p>All the campaigns mentioned above are pre-built in our platform. Just turn them on, customize the messages, and let automation handle the rest.</p>
      </div>
    `
  }
};

const BlogArticle = () => {
  const { slug } = useParams();
  const { formatPrice } = useCurrency();
  const article = fullArticles[slug];

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-[#FF5A3D] hover:underline">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Article Schema
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Person",
      "name": article.author || "Local Business Heroes"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Local Business Heroes",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_localbizheroes/artifacts/792ds0q9_LBH%20Favicon.png"
      }
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" },
    { name: article.title }
  ];

  return (
    <>
      <SEO 
        title={`${article.title} | Local Business Heroes Blog`}
        description={article.excerpt}
        keywords={`${article.category.toLowerCase()}, local business tips, business growth, ${article.title.toLowerCase()}`}
        url={`/blog/${slug}`}
        image={article.image}
        type="article"
        schemaMarkup={articleSchema}
        breadcrumbs={breadcrumbs}
      />
    <div className="min-h-screen bg-gray-50">
      {/* Back to Blog */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-[#FF5A3D] hover:text-[#E5512E] font-semibold"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to All Articles
          </Link>
        </div>
      </div>

      {/* Hero Image */}
      <div className="relative w-full h-[400px] bg-gray-900">
        <img 
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-4xl mx-auto">
            <Badge className="bg-[#FF5A3D] text-white mb-4">
              {article.category}
            </Badge>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <header className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0E163B] mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {article.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-6">
            <div className="flex items-center space-x-2">
              <User size={18} />
              <span>{article.author}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar size={18} />
              <span>{new Date(article.date).toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock size={18} />
              <span>{article.readTime}</span>
            </div>
          </div>

          <p className="text-xl text-gray-600 leading-relaxed">
            {article.excerpt}
          </p>
        </header>

        {/* Article Body */}
        <div 
          className="prose prose-lg max-w-none
            prose-headings:font-bold prose-headings:text-[#0E163B]
            prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
            prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
            prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
            prose-ul:my-6 prose-li:text-gray-700
            prose-strong:text-[#0E163B] prose-strong:font-semibold
            prose-a:text-[#FF5A3D] prose-a:no-underline hover:prose-a:underline"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* CTA Box */}
        <Card className="mt-12 border-2 border-[#FF5A3D] bg-gradient-to-r from-[#FF5A3D]/5 to-[#FF8C42]/5">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold text-[#0E163B] mb-4">
              Ready to Implement These Strategies?
            </h3>
            <p className="text-gray-700 mb-6 text-lg">
              Get all the automation tools mentioned in this article, pre-built and ready to use.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold text-lg px-8 py-6"
              >
                <a href="/pricing">
                  Start 14-Day Trial for {formatPrice(5)}
                </a>
              </Button>
              <Button 
                asChild
                variant="outline"
                className="border-2 border-[#FF5A3D] text-[#FF5A3D] hover:bg-[#FF5A3D] hover:text-white font-semibold text-lg px-8 py-6"
              >
                <a href="/contact">
                  Talk to an Expert
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Back to Blog Footer */}
        <div className="mt-12 pt-8 border-t text-center">
          <Link 
            to="/blog" 
            className="inline-flex items-center text-[#FF5A3D] hover:text-[#E5512E] font-semibold text-lg"
          >
            <ArrowLeft size={20} className="mr-2" />
            Read More Articles
          </Link>
        </div>
      </article>

      {/* Custom Styles */}
      <style>{`
        .lead {
          font-size: 1.25rem;
          line-height: 1.75;
          color: #374151;
          margin-bottom: 2rem;
          font-weight: 500;
        }
        .example-box {
          background: #f3f4f6;
          border-left: 4px solid #FF5A3D;
          padding: 1.5rem;
          margin: 2rem 0;
          border-radius: 0.5rem;
        }
        .example-box h4 {
          margin-top: 0;
          margin-bottom: 0.75rem;
          color: #0E163B;
          font-size: 1.125rem;
        }
        .example-box p {
          margin-bottom: 0.5rem;
        }
        .checklist {
          list-style: none;
          padding-left: 0;
        }
        .checklist li {
          padding-left: 2rem;
          position: relative;
          margin-bottom: 0.75rem;
        }
        .checklist li:before {
          content: "✓";
          position: absolute;
          left: 0;
          color: #FF5A3D;
          font-weight: bold;
          font-size: 1.25rem;
        }
        .cta-box {
          background: linear-gradient(135deg, #FF5A3D 0%, #FF8C42 100%);
          color: white;
          padding: 2rem;
          border-radius: 1rem;
          margin: 3rem 0;
          text-align: center;
        }
        .cta-box h3 {
          color: white;
          margin-bottom: 1rem;
        }
        .stat-box {
          background: #f0fdf4;
          border-left: 4px solid #10b981;
          padding: 1.5rem;
          margin: 1.5rem 0;
          border-radius: 0.5rem;
        }
        .stat-box strong {
          color: #065f46;
          font-size: 1.125rem;
        }
        .simple-table {
          width: 100%;
          margin: 2rem 0;
          border-collapse: collapse;
        }
        .simple-table th,
        .simple-table td {
          padding: 1rem;
          border: 1px solid #e5e7eb;
          text-align: left;
        }
        .simple-table th {
          background: #f9fafb;
          font-weight: 600;
          color: #0E163B;
        }
      `}</style>
    </div>
    </>
  );
};

export default BlogArticle;
